import React from "react";

import { CodeBlock, ParamRow, RichText, ToolCard } from "./McpParts";

export { RichText };

const FamilyCell = ({ value, isName }) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return (
      <span className="om-mcp-family-empty" aria-label="None">
        &ndash;
      </span>
    );
  }

  if (Array.isArray(value)) {
    return (
      <span className="om-mcp-family-chips">
        {value.map((item, index) => {
          const chip = typeof item === "string" ? { v: item } : item;
          return (
            <React.Fragment key={chip.v}>
              {index > 0 && (
                <span className="om-mcp-family-sep" aria-hidden="true">
                  ,
                </span>
              )}
              <code className="om-mcp-chip">{chip.v}</code>
              {chip.optional && (
                <span className="om-mcp-opt om-mcp-family-opt">optional</span>
              )}
            </React.Fragment>
          );
        })}
      </span>
    );
  }

  if (isName) {
    return <code className="om-mcp-family-name">{value}</code>;
  }

  return value;
};

const FamilyTable = ({ table, fallbackLabel }) => {
  const [nameColumn, ...otherColumns] = table.columns;

  return (
    <>
      {table.title && (
        <h4 className="om-mcp-params-label om-mcp-family-section">
          {table.title}
        </h4>
      )}
      <div
        className="om-mcp-family-scroll"
        role="region"
        aria-label={table.title || fallbackLabel}
        tabIndex={0}
      >
        <table className="om-mcp-family-table">
          {table.columns.some((column) => column.width) && (
            <colgroup>
              {table.columns.map((column) => (
                <col key={column.key} style={{ width: column.width }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              {table.columns.map((column) => (
                <th key={column.key} scope="col">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row[nameColumn.key]}>
                <th scope="row">
                  <FamilyCell value={row[nameColumn.key]} isName />
                </th>
                {otherColumns.map((column) => (
                  <td key={column.key}>
                    <FamilyCell value={row[column.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

/** Single-table families use columns/rows; multi-table families use tables. */
const getTables = (tool) =>
  tool.tables || [{ columns: tool.columns, rows: tool.rows }];

export const ToolFamilyCard = ({ tool }) => (
  <article className="om-mcp-card om-mcp-tool om-mcp-family">
    <div className="om-mcp-tool-head">
      <code className="om-mcp-tool-name">{tool.name}</code>
      <span className={`om-mcp-badge om-mcp-badge-${tool.categoryKey}`}>
        {tool.category}
      </span>
    </div>

    <p className="om-mcp-tool-desc">
      <RichText parts={tool.description} />
    </p>

    {tool.sharedParams && tool.sharedParams.length > 0 && (
      <>
        <h4 className="om-mcp-params-label om-mcp-family-section">
          Shared parameters
        </h4>
        <ul className="om-mcp-params om-mcp-family-shared">
          {tool.sharedParams.map((param) => (
            <ParamRow key={param.name} {...param} />
          ))}
        </ul>
      </>
    )}

    {getTables(tool).map((table, index) => (
      <FamilyTable
        key={table.title || index}
        table={table}
        fallbackLabel={`${tool.name} tools`}
      />
    ))}

    {tool.callout && (
      <div className="om-mcp-watch">
        <h4 className="om-mcp-watch-title">{tool.callout.title}</h4>
        <p className="om-mcp-watch-body">
          <RichText parts={tool.callout.parts} />
        </p>
      </div>
    )}

    {tool.example && (
      <div className="om-mcp-family-example">
        <div className="om-mcp-example-label">
          Example arguments
          {tool.exampleTool && (
            <>
              {" "}
              &mdash; <code>{tool.exampleTool}</code>
            </>
          )}
        </div>
        <CodeBlock
          code={tool.example}
          mode="json"
          label={`${tool.exampleTool || tool.name} example arguments`}
        />
      </div>
    )}
  </article>
);

/** Picks the right card for a tool entry. Use this in the TOOL_GROUPS map. */
export const ToolEntry = ({ tool }) =>
  tool.kind === "family" ? (
    <ToolFamilyCard tool={tool} />
  ) : (
    <ToolCard tool={tool} />
  );

/** Counts every callable tool: each row of a family counts as one tool. */
export const countTools = (groups) =>
  groups.reduce(
    (total, group) =>
      total +
      group.tools.reduce(
        (sum, tool) =>
          sum +
          (tool.kind === "family"
            ? getTables(tool).reduce((n, table) => n + table.rows.length, 0)
            : 1),
        0
      ),
    0
  );

export const isStackedGroup = (group) =>
  group.tools.length === 1 || group.tools.some((tool) => tool.kind === "family");